import { useCallback, useEffect, useState } from 'react';

/**
 * Move-history cursor. Browsing earlier positions is tied to the live ply it
 * started from, so any new move snaps the board back to the live position.
 */
export function useHistoryCursor(livePly: number) {
  const [cursor, setCursor] = useState<{ ply: number; at: number } | null>(null);
  const ply = cursor && cursor.at === livePly ? Math.min(cursor.ply, livePly) : livePly;

  const select = useCallback(
    (next: number) => setCursor(next >= livePly ? null : { ply: Math.max(0, next), at: livePly }),
    [livePly],
  );
  const backToLive = useCallback(() => setCursor(null), []);

  return { ply, isLive: ply === livePly, select, backToLive };
}

/**
 * Opens a result dialog shortly after a game ends. `key` identifies the
 * finished game (null while playing); closing hides it for that game only.
 */
export function useResultDialog(key: string | null, delayMs = 650) {
  const [readyFor, setReadyFor] = useState<string | null>(null);
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);

  useEffect(() => {
    if (key === null) return;
    const id = window.setTimeout(() => setReadyFor(key), delayMs);
    return () => window.clearTimeout(id);
  }, [key, delayMs]);

  const close = useCallback(() => setDismissedFor(key), [key]);
  return { open: key !== null && readyFor === key && dismissedFor !== key, close };
}

/** Returns `value` until `ms` after it last changed, then null. */
export function useTransient<T>(value: T | null, ms: number): T | null {
  const [expired, setExpired] = useState<T | null>(null);
  useEffect(() => {
    if (value === null) return;
    const id = window.setTimeout(() => setExpired(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return value !== null && expired !== value ? value : null;
}
